"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ChevronDown, ChevronUp } from "lucide-react";
import AppSelect from "../../components/AppSelect";
import CreatableCombobox from "../../components/CreatableCombobox";
import AssistantOnboardingHeader from "../../components/AssistantOnboardingHeader";
import providersService from "../../api/services/providersService";
import useEditFlow from "../../hooks/useEditFlow";
import { useToast } from "../../hooks/useToast";
import { getStoredUserIdentifier } from "../../utils/authSession";
import { validateUploadFile } from "../../utils/directUpload";
import { ACCEPTED_IMAGE_ACCEPT } from "../../constants/imageUpload";
import {
  VEHICLE_BRANDS,
  VEHICLE_BRAND_MAX_LENGTH,
  VEHICLE_TYPES,
  VEHICLE_YEARS,
} from "../../constants/vehicle";

const getFilenameFromUrl = (value = "") => {
  if (typeof value !== "string") return "";
  const trimmed = value.split("?")[0];
  const parts = trimmed.split("/");
  return parts[parts.length - 1] || "";
};

const resolveFileName = (source) => {
  if (!source) return "";
  if (typeof source === "string") return getFilenameFromUrl(source) || source;
  if (source.file) return resolveFileName(source.file);
  if (typeof source.name === "string" && source.name.trim()) return source.name.trim();
  if (typeof source.url === "string") return getFilenameFromUrl(source.url);
  return "";
};

const unwrapVehicleResponse = (response) => {
  if (!response) return {};
  return (
    response.data?.data?.vehicle ||
    response.data?.vehicle ||
    response.data?.data ||
    response.data ||
    response.vehicle ||
    response
  );
};


const ensureCurrentOption = (options, currentValue) => {
  if (!currentValue || options.includes(currentValue)) return options;
  return [currentValue, ...options];
};

const incrementNumericValue = (value = "", step = 1) => {
  const parsed = Number(value || 0);
  return String((Number.isFinite(parsed) ? parsed : 0) + step);
};

const decrementNumericValue = (value = "", step = 1) => {
  const parsed = Number(value || 0);
  const nextValue = Math.max(0, (Number.isFinite(parsed) ? parsed : 0) - step);
  return nextValue > 0 ? String(nextValue) : "";
};

export default function VehicleInformation({ currentUser }) {
  const navigate = useNavigate();
  const location = useLocation();
  const fromProfile = Boolean(location.state?.fromProfile);
  const isEditingProfile = useEditFlow(fromProfile);
  const toast = useToast();
  const docInputRef = useRef(null);
  const imageInputRef = useRef(null);
  const userIdentifier = currentUser?.id || currentUser?.email || getStoredUserIdentifier() || "guest";
  const storageKey = `assistantVehicleDraft_${userIdentifier}`;

  const [vehicleType, setVehicleType] = useState("");
  const [capacity, setCapacity] = useState("");
  const [brand, setBrand] = useState("");
  const [year, setYear] = useState("");
  const [documents, setDocuments] = useState(null);
  const [images, setImages] = useState(null);
  const [documentName, setDocumentName] = useState("");
  const [imageName, setImageName] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isHydrated, setIsHydrated] = useState(false);
  const [existingDocumentName, setExistingDocumentName] = useState("");
  const [existingImageName, setExistingImageName] = useState("");
  const [hasLoadedExistingVehicle, setHasLoadedExistingVehicle] = useState(false);

  const vehicleTypeOptions = useMemo(
    () => ensureCurrentOption(VEHICLE_TYPES, vehicleType),
    [vehicleType]
  );
  const vehicleYearOptions = useMemo(
    () => ensureCurrentOption(VEHICLE_YEARS, year),
    [year]
  );

  const handleFileChange = (setter) => (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    // Images only — the API has always rejected PDFs, so accepting them here
    // only produced a failure after the upload.
    const validationError = validateUploadFile(file);
    if (validationError) {
      toast.error(validationError);
      return;
    }
    setter(file);
  };

  useEffect(() => {
    if (isEditingProfile) {
      setIsHydrated(true);
      return;
    }
    const raw = localStorage.getItem(storageKey);
    if (!raw) {
      setVehicleType("");
      setCapacity("");
      setBrand("");
      setYear("");
      setDocuments(null);
      setImages(null);
      setDocumentName("");
      setImageName("");
      setIsHydrated(true);
      return;
    }
    try {
      const parsed = JSON.parse(raw);
      setVehicleType(parsed.vehicleType || "");
      setCapacity(parsed.capacity || "");
      setBrand(parsed.brand || "");
      setYear(parsed.year || "");
      setDocumentName(parsed.documentName || "");
      setImageName(parsed.imageName || "");
    } catch (err) {
      // Ignore malformed saved drafts
    } finally {
      setIsHydrated(true);
    }
  }, [isEditingProfile, storageKey]);

  useEffect(() => {
    if (!isHydrated || isEditingProfile) return;
    const draft = {
      vehicleType,
      capacity,
      brand,
      year,
      documentName,
      imageName,
    };
    localStorage.setItem(storageKey, JSON.stringify(draft));
  }, [vehicleType, capacity, brand, year, documentName, imageName, isHydrated, storageKey, isEditingProfile]);

  useEffect(() => {
    if (hasLoadedExistingVehicle) return;
    let active = true;
    const fetchVehicle = async () => {
      try {
        const response = await providersService.getVehicle();
        if (!active) return;
        const data = unwrapVehicleResponse(response);
        const resolvedType =
          data.type || data.vehicleType || data.transportType || "";
        const resolvedBrand = data.brand || data.make || "";
        const resolvedCapacity =
          data.maxPassengers ??
          data.max_passengers ??
          data.capacity ??
          data.maxPassenger ??
          "";
        const resolvedYear =
          data.year || data.vehicleYear || data.vehicle_year || "";

        if (resolvedType) setVehicleType(resolvedType);
        if (resolvedBrand) setBrand(resolvedBrand);
        if (resolvedCapacity !== "" && Number.isFinite(Number(resolvedCapacity))) {
          setCapacity(String(resolvedCapacity));
        }
        if (resolvedYear !== "" && Number.isFinite(Number(resolvedYear))) {
          setYear(String(resolvedYear));
        }

        const documentSources = Array.isArray(data.vehicleDocuments)
          ? data.vehicleDocuments
          : Array.isArray(data.documents)
            ? data.documents
            : Array.isArray(data.vehicle_document)
              ? data.vehicle_document
              : [];
        const imageSources = Array.isArray(data.vehicleImages)
          ? data.vehicleImages
          : Array.isArray(data.images)
            ? data.images
            : Array.isArray(data.vehicle_images)
              ? data.vehicle_images
              : [];
        const firstDocument = documentSources[0];
        const firstImage = imageSources[0];
        const resolvedDocumentName = resolveFileName(firstDocument);
        const resolvedImageName = resolveFileName(firstImage);
        if (resolvedDocumentName) setExistingDocumentName(resolvedDocumentName);
        if (resolvedImageName) setExistingImageName(resolvedImageName);
      } catch (err) {
        // Ignore load errors
      } finally {
        if (active) {
          setHasLoadedExistingVehicle(true);
        }
      }
    };
    fetchVehicle();
    return () => {
      active = false;
    };
  }, [hasLoadedExistingVehicle]);

  const parsedCapacity = Number(capacity);
  const parsedYear = Number(year);
  const hasExistingDocument = Boolean(existingDocumentName);
  const hasExistingImage = Boolean(existingImageName);
  const isFormComplete =
    Boolean(vehicleType) &&
    Boolean(brand) &&
    Number.isFinite(parsedCapacity) &&
    parsedCapacity > 0 &&
    Number.isFinite(parsedYear) &&
    parsedYear > 0 &&
    (Boolean(documents) || hasExistingDocument) &&
    (Boolean(images) || hasExistingImage);

  const handleVerify = async () => {
    if (!vehicleType || !capacity || !brand || !year) {
      toast.error("Please complete all fields.");
      return;
    }
    const hasDocumentUpload = Boolean(documents) || hasExistingDocument;
    const hasImageUpload = Boolean(images) || hasExistingImage;
    if (!hasDocumentUpload || !hasImageUpload) {
      toast.error("Please upload both vehicle documents and photos.");
      return;
    }
    if (!Number.isFinite(parsedCapacity) || parsedCapacity <= 0) {
      toast.error("Please enter a valid passenger capacity.");
      return;
    }
    if (!Number.isFinite(parsedYear) || parsedYear <= 0) {
      toast.error("Please select a valid vehicle year.");
      return;
    }

    setIsSubmitting(true);
    try {
      if (isEditingProfile) {
        await providersService.updateVehicle({
          type: vehicleType,
          maxPassengers: parsedCapacity,
          brand,
          year: parsedYear,
          vehicleDocuments: documents ? [documents] : [],
          vehicleImages: images ? [images] : [],
        });
      } else {
        await providersService.submitVehicle({
          type: vehicleType,
          maxPassengers: parsedCapacity,
          brand,
          year: parsedYear,
          vehicleDocuments: documents ? [documents] : [],
          vehicleImages: images ? [images] : [],
        });
      }
      localStorage.removeItem(storageKey);
      const navOptions = isEditingProfile ? { state: { fromProfile: true } } : undefined;
      navigate("/assistant/pricing", navOptions);
    } catch (err) {
      toast.apiError(err, "Unable to submit vehicle details. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white text-gray-900">
      <AssistantOnboardingHeader />

      <main className="mx-auto flex max-w-4xl flex-col px-4 pb-16 pt-10">
        <div className="text-center space-y-6">
          <h1 className="text-3xl font-semibold text-gray-900">Vehicle Information</h1>
          <div className="mx-auto h-2 w-full max-w-2xl overflow-hidden rounded-full bg-[#d3d6dd]">
            <div className="h-full w-[80%] rounded-full bg-[#7a0051]" />
          </div>
          <p className=" text- font-medium text-gray-600">
            Please provide the information requested below.
          </p>
        </div>

        <div className="mt-10 space-y-6">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <label className="text- font-medium text-gray-800">
                Vehicle Type <span className="font-bold text-red-600">*</span>
              </label>
              <AppSelect
                value={vehicleType}
                onChange={(value) => {
                  setVehicleType(value);
                }}
                options={vehicleTypeOptions}
                placeholder="Select Vehicle Type"
                buttonClassName="!rounded-lg !px-4 !py-3 !text-base !font-medium !text-gray-800"
                optionsClassName="!rounded-xl"
                optionClassName="!text-sm"
              />
            </div>

            <div className="space-y-2">
              <label className="text- font-medium text-gray-800">
                Maximum Passenger Capacity <span className="font-bold text-red-600">*</span>
              </label>
              <div className="relative">
                <input
                  type="text"
                  value={capacity}
                  onChange={(e) => {
                    setCapacity(e.target.value.replace(/\D/g, ""));
                  }}
                  inputMode="numeric"
                  placeholder="e.g 4"
                  className="w-full rounded-lg border border-[#d8dbe0] bg-white px-4 py-3 pr-12 text-base font-medium text-gray-800 placeholder:text-gray-400 focus:border-[#7a0051] focus:outline-none focus:ring-1 focus:ring-[#7a0051]/30"
                />
                <div className="absolute inset-y-0 right-3 flex flex-col items-center justify-center gap-0.5">
                  <button
                    type="button"
                    onClick={() => {
                      setCapacity((prev) => incrementNumericValue(prev));
                    }}
                    className="flex h-4 w-4 items-center justify-center text-[#98A2B3] transition hover:text-[#344054]"
                    aria-label="Increase passenger capacity"
                  >
                    <ChevronUp className="h-3.5 w-3.5" />
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setCapacity((prev) => decrementNumericValue(prev));
                    }}
                    className="flex h-4 w-4 items-center justify-center text-[#98A2B3] transition hover:text-[#344054]"
                    aria-label="Decrease passenger capacity"
                  >
                    <ChevronDown className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <label className="text- font-medium text-gray-800">
                Vehicle Brand <span className="font-bold text-red-600">*</span>
              </label>
              <CreatableCombobox
                value={brand}
                onChange={setBrand}
                options={VEHICLE_BRANDS}
                placeholder="Select or type vehicle brand"
                maxLength={VEHICLE_BRAND_MAX_LENGTH}
              />
            </div>

            <div className="space-y-2">
              <label className="text- font-medium text-gray-800">
                Vehicle Year <span className="font-bold text-red-600">*</span>
              </label>
              <AppSelect
                value={year}
                onChange={(value) => {
                  setYear(value);
                }}
                options={vehicleYearOptions}
                placeholder="Select vehicle year"
                buttonClassName="!rounded-lg !px-4 !py-3 !text-base !font-medium !text-gray-800"
                optionsClassName="!rounded-xl"
                optionClassName="!text-sm"
              />
            </div>
          </div>

          <div className="space-y-4">
            <div className="rounded-xl border-2 border-dashed border-[#cbd0d6] p-6 text-center">
              <p className="text-xl font-semibold text-gray-900">
                Upload Vehicle Documents <span className="font-bold text-red-600">*</span>
              </p>
              <p className="mt-1 text- font- text-gray-500">
                Particulars, State permits, Drivers Licence. Must be clear and readable.
              </p>
              <div className="mt-4">
                <input
                  ref={docInputRef}
                  type="file"
                  accept={ACCEPTED_IMAGE_ACCEPT}
                  className="hidden"
                  onChange={(e) => {
                    handleFileChange(setDocuments)(e);
                    const file = e.target.files?.[0];
                    if (file) {
                      setDocumentName(file.name || "");
                      setExistingDocumentName("");
                    }
                  }}
                />
                <button
                  type="button"
                  onClick={() => docInputRef.current?.click()}
                  className="max-w-full break-all rounded-lg bg-[#d4b4c5] px-6 py-2 text-sm font-semibold text-white transition hover:bg-[#c299b2] focus:outline-none focus:ring-2 focus:ring-[#7a0051]/50 focus:ring-offset-2"
                >
                  {documents?.name ||
                    (documentName
                      ? `${documentName} (reselect)`
                      : existingDocumentName
                        ? `${existingDocumentName} (on file)`
                        : "Browse File")}
                </button>
              </div>
            </div>

            <div className="rounded-xl border-2 border-dashed border-[#cbd0d6] p-6 text-center">
              <p className="text-xl font-semibold text-gray-900">
                Vehicle Images <span className="font-bold text-red-600">*</span>
              </p>
              <p className="mt-1 text- font- text-gray-500">
                Clear photos of all sides of the vehicle.
              </p>
              <div className="mt-4">
                <input
                  ref={imageInputRef}
                  type="file"
                  accept={ACCEPTED_IMAGE_ACCEPT}
                  className="hidden"
                  onChange={(e) => {
                    handleFileChange(setImages)(e);
                    const file = e.target.files?.[0];
                    if (file) {
                      setImageName(file.name || "");
                      setExistingImageName("");
                    }
                  }}
                />
                <button
                  type="button"
                  onClick={() => imageInputRef.current?.click()}
                  className="max-w-full break-all rounded-lg bg-[#d4b4c5] px-6 py-2 text-sm font-semibold text-white transition hover:bg-[#c299b2] focus:outline-none focus:ring-2 focus:ring-[#7a0051]/50 focus:ring-offset-2"
                >
                  {images?.name ||
                    (imageName
                      ? `${imageName} (reselect)`
                      : existingImageName
                        ? `${existingImageName} (on file)`
                        : "Browse File")}
                </button>
              </div>
            </div>
          </div>


          <div className="flex flex-col gap-3 pt-4">
          <button
            type="button"
            onClick={handleVerify}
            className={`w-full rounded-lg px-4 py-3 text-center text-base font-semibold text-white shadow-sm transition ${
              isFormComplete
                ? "bg-[#61013B] hover:bg-[#4f0130]"
                : "bg-[#caa8b8] hover:bg-[#b792a3]"
            }`}
            disabled={!isFormComplete || isSubmitting}
          >
            {isSubmitting ? "Submitting..." : "Continue"}
          </button>
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="w-full rounded-lg border border-[#7a0051] px-4 py-3 text-center text-base font-semibold text-[#7a0051] transition hover:bg-[#f7eef4]"
            >
              Back
            </button>
          </div>
        </div>
      </main>
    </div>
  );
}
